package com.milta.core.observability

import com.milta.core.auth.JwtParser
import com.milta.core.auth.TokenStorage
import com.milta.core.network.ApiException
import io.ktor.client.plugins.ResponseException
import io.ktor.client.network.sockets.ConnectTimeoutException
import io.ktor.client.network.sockets.SocketTimeoutException
import io.ktor.client.plugins.HttpRequestTimeoutException
import kotlinx.io.IOException
import kotlin.coroutines.cancellation.CancellationException

/**
 * Single entry point for sending *unexpected* errors to GlitchTip (Sentry-compatible SDK).
 *
 * Only genuine bugs become events: crashes, unforeseen exceptions and server answers >= 500.
 * Business errors (4xx) and the mere absence of network are filtered out in [report].
 *
 * The SDK itself lives in androidMain/iosMain only (initialised by `MiltaApplication` /
 * `doInitKoin`); the web target has a no-op actual — the web dashboard is out of scope. With an
 * empty DSN the SDK is never started and every call below is a harmless no-op.
 *
 * Contract shared with the API: the event user carries the account id ONLY (never email, phone,
 * name or token) and the scope carries the `tenant_id` and `role` tags.
 */
object ErrorReporting {

    /** Reports an exception caught at a failure point (`onFailure`, `catch`...), if unexpected. */
    fun report(throwable: Throwable, source: String? = null) {
        if (!throwable.isUnexpected()) return
        platformCaptureException(throwable, source, status = null)
    }

    /**
     * Reports a server answer with status >= 500. Only the method and a normalised path are kept:
     * query strings can carry personal data (e.g. phone/email availability checks) and raw ids
     * would split a single problem into one issue per entity.
     */
    fun reportServerError(method: String, rawPath: String, status: Int) {
        if (status < SERVER_ERROR_MIN) return
        val path = normalisePath(rawPath)
        platformCaptureException(ServerErrorException(status, method, path), source = "http", status = status)
    }

    /** Sets the user context (account id, tenant_id, role) from the access token claims. */
    fun setUserFromAccessToken(accessToken: String) {
        val accountId = JwtParser.extractSubject(accessToken) ?: return clearUser()
        platformSetUser(
            accountId = accountId,
            tenantId = JwtParser.extractTenantId(accessToken),
            role = JwtParser.extractRole(accessToken),
        )
    }

    /** Re-applies the user context at app start when a session is already stored. */
    fun restoreUser(tokenStorage: TokenStorage) {
        tokenStorage.getAccessToken()?.let(::setUserFromAccessToken)
    }

    fun clearUser() = platformClearUser()

    internal fun Throwable.isUnexpected(): Boolean = when {
        this is CancellationException -> false
        // 4xx are business answers; >= 500 are already reported once, at the HTTP layer.
        this is ApiException || this is ResponseException -> false
        isConnectivityFailure() -> false
        else -> true
    }

    /** True when the failure is "no usable connection" (offline, DNS, timeout...) — not a bug. */
    internal fun Throwable.isConnectivityFailure(): Boolean = generateSequence(this) { it.cause }
        .take(CAUSE_DEPTH_LIMIT)
        .any { cause ->
            cause is IOException ||
                cause is HttpRequestTimeoutException ||
                cause is ConnectTimeoutException ||
                cause is SocketTimeoutException ||
                // Darwin engine wraps NSURLError (offline, DNS, timeout...) in its own type.
                cause::class.simpleName == "DarwinHttpRequestException"
        }

    private fun normalisePath(rawPath: String): String =
        rawPath.substringBefore('?').split('/').joinToString("/") { segment ->
            if (segment.isNotEmpty() && (segment.all { it.isDigit() } || UUID_REGEX.matches(segment))) "{id}" else segment
        }

    private val UUID_REGEX = Regex("[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}")
    private const val SERVER_ERROR_MIN = 500
    private const val CAUSE_DEPTH_LIMIT = 8
}

/** Synthetic exception for a server answer >= 500, so it groups by status/method/path. */
class ServerErrorException(val status: Int, method: String, path: String) :
    Exception("HTTP $status $method $path")

internal expect fun platformCaptureException(throwable: Throwable, source: String?, status: Int?)

internal expect fun platformSetUser(accountId: String, tenantId: String?, role: String?)

internal expect fun platformClearUser()
